import React, { ReactNode } from 'react'
import { motion } from 'framer-motion'

interface LegalPageLayoutProps {
    title: string
    lastUpdated: string
    children: ReactNode
}

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({ title, lastUpdated, children }) => {
    return (
        <div className="min-h-screen bg-[#faf7f2] selection:bg-[#4a7c5e]/10">
            {/* Decorative background elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[10%] -right-[10%] w-[40%] h-[40%] bg-[#4a7c5e]/5 rounded-full blur-[120px]" />
                <div className="absolute -bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-[#c9903a]/5 rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 container mx-auto px-6 py-20 max-w-4xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Header */}
                    <header className="mb-16 border-b border-slate-200 pb-12">
                        <h1 className="text-5xl md:text-6xl font-black text-[#1a2740] mb-6 tracking-tight font-serif italic">
                            {title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-slate-500 font-bold uppercase tracking-widest text-sm">
                            <span className="bg-[#4a7c5e]/10 text-[#4a7c5e] px-4 py-1.5 rounded-full border border-[#4a7c5e]/20">
                                Official Document
                            </span>
                            <span>Last Updated: {lastUpdated}</span>
                        </div>
                    </header>

                    {/* Main Content */}
                    <main className="prose prose-lg prose-slate max-w-none 
            prose-headings:text-[#1a2740] prose-headings:font-black prose-headings:font-serif
            prose-p:text-slate-600 prose-p:leading-relaxed prose-p:text-xl
            prose-li:text-slate-600 prose-li:text-xl prose-li:leading-relaxed
            prose-strong:text-[#1a2740] prose-strong:font-black
            prose-a:text-[#4a7c5e] prose-a:font-bold hover:prose-a:underline transition-all
            prose-blockquote:border-l-[#4a7c5e] prose-blockquote:bg-[#e8f2ec] prose-blockquote:p-8 prose-blockquote:rounded-r-2xl prose-blockquote:font-serif prose-blockquote:italic
            ">
                        {children}
                    </main>

                    {/* Footer of the legal doc */}
                    <footer className="mt-20 pt-12 border-t border-slate-200">
                        <div className="bg-[#1a2740] text-white p-10 rounded-3xl shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-1/2 h-full bg-[#4a7c5e] opacity-10 blur-3xl pointer-events-none" />
                            <div className="relative z-10 space-y-4">
                                <h3 className="text-2xl font-black mb-2 font-serif uppercase tracking-wider">Contact Administration</h3>
                                <p className="text-slate-300 text-lg max-w-xl leading-relaxed">
                                    If you have questions regarding this document or how we handle your information,
                                    please reach out to our legal and administrative team directly.
                                </p>
                                <div className="pt-4">
                                    <a
                                        href="mailto:support@stillwatersacademy.com"
                                        className="inline-flex items-center gap-3 bg-[#4a7c5e] hover:bg-[#3d664d] text-white font-bold py-4 px-8 rounded-full transition-all shadow-lg"
                                    >
                                        support@stillwatersacademy.com
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">
                            Still Waters Academy &copy; {new Date().getFullYear()} &bull; Still Waters LLC (Wyoming, USA)
                        </div>
                    </footer>
                </motion.div>
            </div>
        </div>
    )
}

export default LegalPageLayout
